import type { TFunction } from 'i18next';
import {
    coverageCellKey,
    type AdminRegistryApp,
    type RoleKeyRow,
    type RoleMapCoverage,
} from '@/api/console';
import type { AppCoverageRow, CoverageTab, RoleKeyByKey } from './types';

/**
 * EAA-1817: pure derivations for the coverage board — no React, no fetching,
 * so they're trivial to reason about and re-test in isolation. Every function
 * here composes ONLY server-computed fields (`RoleMapCoverage.cells[*].state`,
 * `AdminRegistryApp.catalog_role_count`, …); nothing recomputes a health state
 * the server already resolved.
 */

/** First 3 chars of the app code, lowercased — the small mono "tile" glyph in
 *  the rail and the editor header (design frames render it as a short lowercase
 *  monospace code, e.g. "lms", regardless of how long the real `Application.code`
 *  turns out to be). */
export function appTileLabel(code: string): string {
    return code.slice(0, 3).toLowerCase();
}

/** `role_key -> RoleKeyRow` for O(1) name/description/count lookups. */
export function buildRoleKeyByKey(roleKeys: RoleKeyRow[]): RoleKeyByKey {
    return new Map(roleKeys.map((row) => [row.key, row]));
}

/**
 * EAA-1817: rolls each application's row of the matrix up into one summary
 * for the left rail.
 *
 * `coveredCount` / `pausedCellCount` are counted directly off
 * `RoleMapCoverage.cells` (server-computed states) — this file never guesses
 * at a cell's health itself, only tallies what the server already decided.
 *
 * `dotTone` precedence (documented here because none of it is server-shaped
 * — the coverage endpoint has no per-app rollup of its own):
 *   1. `!enabled`      -> 'muted' (unapproved/disabled app; wins regardless of
 *                          links, since a disabled app provisions nothing)
 *   2. paused cell > 0 -> 'off'   (a real, server-flagged broken link)
 *   3. fully covered   -> 'ok'
 *   4. otherwise       -> 'warn'  (partial coverage, 0 <= covered < total)
 */
export function computeAppCoverageRows(
    apps: AdminRegistryApp[],
    coverage: RoleMapCoverage
): AppCoverageRow[] {
    const totalRoleKeys = coverage.role_keys.length;
    return apps.map((app) => {
        let coveredCount = 0;
        let pausedCellCount = 0;
        for (const roleKey of coverage.role_keys) {
            const cell = coverage.cells[coverageCellKey(app.code, roleKey)];
            if (!cell || cell.state === 'none') continue;
            coveredCount += 1;
            if (cell.state === 'paused') pausedCellCount += 1;
        }

        let dotTone: AppCoverageRow['dotTone'];
        if (!app.enabled) {
            dotTone = 'muted';
        } else if (pausedCellCount > 0) {
            dotTone = 'off';
        } else if (totalRoleKeys > 0 && coveredCount === totalRoleKeys) {
            dotTone = 'ok';
        } else {
            dotTone = 'warn';
        }

        return {
            code: app.code,
            name: app.name,
            enabled: app.enabled,
            catalogRoleCount: app.catalog_role_count,
            coveredCount,
            totalRoleKeys,
            pausedCellCount,
            supportsRoleManagement: app.supports_role_management,
            dotTone,
        };
    });
}

/**
 * EAA-1817: the two rail tabs beyond "Tất cả" — see `CoverageTab`'s docstring
 * for why a merely-partial-but-healthy app has no tab of its own.
 */
export function matchesTab(row: AppCoverageRow, tab: CoverageTab): boolean {
    if (tab === 'all') return true;
    if (tab === 'needsAttention') return row.pausedCellCount > 0;
    return row.coveredCount === 0;
}

export function countByTab(rows: AppCoverageRow[], tab: CoverageTab): number {
    return rows.filter((row) => matchesTab(row, tab)).length;
}

export function matchesSearch(row: AppCoverageRow, normalizedSearch: string): boolean {
    if (!normalizedSearch) return true;
    return `${row.code} ${row.name}`.toLowerCase().includes(normalizedSearch);
}

/** EAA-1817: relative-age label for the editor header's "Danh mục đồng bộ N
 *  ngày trước" line (design frame 55).
 *
 * The VERB is part of the label, not of the sentence around it: the caller's
 * template is bare ("Danh mục {{ageLabel}}"), because the null case reads
 * "chưa từng đồng bộ" and a template that supplied its own "đồng bộ" produced
 * "Danh mục vai trò đồng bộ chưa từng đồng bộ" — which is what shipped. — a small local duplicate of `apps/catalogAge.ts`'s
 *  formatter (that file is owned by another in-flight ticket's worker; not
 *  imported across that boundary — see this ticket's file-ownership split).
 *
 * EAA-1845: a plain (non-hook) helper can't call `useTranslation()` itself, so
 * the caller (a hook/component that already has `t`) passes it in. */
export function formatRelativeDays(iso: string | null, t: TFunction): string {
    if (!iso) return t('console.roleCoverage.catalogAge.never');
    const syncedAt = new Date(iso).getTime();
    if (Number.isNaN(syncedAt)) return t('console.roleCoverage.catalogAge.never');
    const diffDays = Math.floor((Date.now() - syncedAt) / (24 * 60 * 60 * 1000));
    if (diffDays <= 0) return t('console.roleCoverage.catalogAge.today');
    if (diffDays === 1) return t('console.roleCoverage.catalogAge.oneDayAgo');
    return t('console.roleCoverage.catalogAge.daysAgo', { count: diffDays });
}
